// 작업자 호소 입력 form 의 신규/수정 모드 토글 컨트롤러.
//
// 동작:
//   - 'worker-complaint:edit' CustomEvent 를 document 에서 listen
//   - 받은 데이터로 form fields prefill + 모드 전환 (헤더/취소버튼/저장버튼/action/method 변경)
//   - [취소] 클릭 시 form reset + 신규 모드 복귀
//   - 저장은 form 의 submit 그대로 (POST create or PATCH update — _method 로 분기)
//
// values:
//   createUrl — 신규 POST 경로
//   updateUrl — 수정 PATCH 경로
import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "header",            // 섹션 헤더 텍스트
    "cancelButton",      // [취소] 버튼 (신규 모드 hidden, 수정 모드 visible)
    "submitButton",      // [저장] 버튼 (텍스트 토글)
    "complaintIdInput",  // hidden complaint_id
    "methodInput"        // hidden _method ('' 또는 'patch')
  ]

  static values = {
    createUrl: String,
    updateUrl: String,
    createHeader: { type: String, default: "새 호소 입력" },
    editHeader: { type: String, default: "호소 수정" },
    createSubmit: { type: String, default: "작업자 호소 저장" },
    editSubmit: { type: String, default: "수정 저장" }
  }

  connect() {
    document.addEventListener("worker-complaint:edit", this.enterEditMode)
  }

  disconnect() {
    document.removeEventListener("worker-complaint:edit", this.enterEditMode)
  }

  enterEditMode = (event) => {
    const d = event.detail
    const form = this.element

    // 1) form action/method 전환 (POST create → PATCH update)
    form.action = this.updateUrlValue
    if (this.hasMethodInputTarget) this.methodInputTarget.value = "patch"
    if (this.hasComplaintIdInputTarget) this.complaintIdInputTarget.value = d.complaintId || ""

    // 2) form fields prefill
    // ── 호소 유무 radio
    const hasComplaint = d.hasComplaint === "true"
    form.querySelectorAll('input[type="radio"][name$="[has_complaint]"]').forEach((r) => {
      r.checked = r.value === (hasComplaint ? "true" : "false")
    })

    // ── 호소 유형 checkbox 3
    const cb = (target) => form.querySelector(`input[data-worker-complaint-form-target="${target}"]`)
    const setCb = (target, v) => { const el = cb(target); if (el) el.checked = v === "true" }
    setCb("typeSymptom", d.hasSymptomComplaint)
    setCb("typeExposure", d.hasExposureConcern)
    setCb("typeOther", d.hasOtherComplaint)

    // ── 호소 증상 select (Fomantic UI)
    const symptomInput = form.querySelector('input[data-worker-complaint-form-target="symptomSelect"]')
    if (symptomInput) {
      const v = d.symptom || ""
      symptomInput.value = v
      const dropdown = symptomInput.closest(".ui.dropdown")
      if (dropdown) {
        const textEl = dropdown.querySelector(".text")
        if (textEl) {
          if (v) { textEl.textContent = v; textEl.classList.remove("default") }
          else {
            const placeholder = dropdown.dataset.dropdownPlaceholderValue || "증상을 선택하세요"
            textEl.textContent = placeholder
            textEl.classList.add("default")
          }
        }
        dropdown.querySelectorAll(".menu .item").forEach((item) => {
          const isActive = item.dataset.value === v
          item.classList.toggle("active", isActive)
          item.classList.toggle("selected", isActive)
        })
      }
    }

    // ── 점검일 calendar
    const cal = form.querySelector('input[name="worker_complaint[occurred_on]"]')
    if (cal) {
      cal.value = d.occurredOn || ""
      const clearBtn = cal.closest("[data-controller='calendar']")
                          ?.querySelector("[data-calendar-target='clearButton']")
      if (clearBtn) clearBtn.style.display = (d.occurredOn ? "" : "none")
    }

    // ── 메모 textarea
    const memo = form.querySelector('textarea[data-worker-complaint-form-target="memoTextarea"]')
    if (memo) memo.value = d.detail || ""

    // 3) 헤더/버튼 모드 전환
    if (this.hasHeaderTarget) this.headerTarget.textContent = this.editHeaderValue
    if (this.hasCancelButtonTarget) this.cancelButtonTarget.style.display = "inline-flex"
    if (this.hasSubmitButtonTarget) this.submitButtonTarget.textContent = this.editSubmitValue

    // 4) cascade 재트리거 — worker-complaint-form 컨트롤러가 활성/비활성 상태 갱신
    const checkedRadio = form.querySelector('input[type="radio"][name$="[has_complaint]"]:checked')
    if (checkedRadio) checkedRadio.dispatchEvent(new Event("change", { bubbles: true }))
    ;["typeSymptom", "typeExposure", "typeOther"].forEach((t) => {
      const el = cb(t)
      if (el) el.dispatchEvent(new Event("change", { bubbles: true }))
    })

    // 5) form 으로 부드럽게 scroll
    form.scrollIntoView({ behavior: "smooth", block: "center" })
  }

  cancel() {
    const form = this.element

    // form reset
    form.reset()

    // 신규 모드로 복귀
    form.action = this.createUrlValue
    if (this.hasMethodInputTarget) this.methodInputTarget.value = ""
    if (this.hasComplaintIdInputTarget) this.complaintIdInputTarget.value = ""
    if (this.hasHeaderTarget) this.headerTarget.textContent = this.createHeaderValue
    if (this.hasCancelButtonTarget) this.cancelButtonTarget.style.display = "none"
    if (this.hasSubmitButtonTarget) this.submitButtonTarget.textContent = this.createSubmitValue

    // worker-complaint-form 의 cascade 재계산 (모든 fields disabled 상태로)
    const radio = form.querySelector('input[type="radio"][name$="[has_complaint]"]:checked')
    if (radio) radio.dispatchEvent(new Event("change", { bubbles: true }))
  }
}
